import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { isDemoMode } from "@/components/DemoBanner";
import { useSEO } from "@/lib/seo";
import { track } from "@/lib/analytics";

export default function Register() {
  useSEO({
    title: "Create your account",
    description: "In early access. Build a custom AAC board for your child or client in under 5 minutes.",
    path: "/register",
  });
  const { user, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inviteCode = params.get("invite");
  const refCode = params.get("ref");
  const successPath = inviteCode
    ? `/invite/${inviteCode}`
    : refCode
    ? `/refer/${refCode}`
    : "/pick-board";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Region scaffolding — adult-account level. Defaults to US so today's
  // signup UX is unchanged for the 100% US user base. When the user
  // picks a non-US country and ALLOW_NON_US_SIGNUP is off (current
  // default), the backend rejects with a friendly waitlist message.
  const [country, setCountry] = useState("US");
  const [stateProvince, setStateProvince] = useState("");
  const [caregiverConsent, setCaregiverConsent] = useState(false);
  const [earlyAccessAck, setEarlyAccessAck] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { track("register_start"); }, []);

  if (user) return <Navigate to={successPath} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!caregiverConsent) {
      setError("Please confirm you are 18 or older and the parent, legal guardian, adult caregiver, or authorized professional responsible for this account.");
      return;
    }
    if (!earlyAccessAck) {
      setError("Please read and check the early-access box — SonSpeak is not a medical, clinical, or emergency tool.");
      return;
    }
    setBusy(true);
    const res = await register(name, email, password, {
      country: country || "US",
      state_province: country === "US" ? (stateProvince.trim().toUpperCase() || null) : null,
      caregiver_consent: true,
    });
    if (res.ok) {
      track("register_complete");
      setBusy(false);
      nav(successPath);
    } else {
      setBusy(false);
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[color:var(--cream)]">
      <div className="brutal-card p-8 w-full max-w-md bg-[color:var(--paper)]">
        <div className="text-center mb-6">
          {isDemoMode() && (
            <div className="inline-flex items-center gap-1.5 bg-[color:var(--coral)] text-white text-[10px] font-extrabold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border-[2px] border-[color:var(--ink)] mb-3" data-testid="register-demo-pill">
              Early access
            </div>
          )}
          <div className="text-5xl mb-2" aria-hidden>🌟</div>
          <h1 className="display text-3xl font-extrabold">Create your account</h1>
          <p className="text-[color:var(--ink-soft)] mt-1">For caregivers, parents, and therapists</p>
          {isDemoMode() && (
            <p className="text-xs text-[color:var(--ink-soft)] mt-2 leading-relaxed" data-testid="register-demo-note">
              SonSpeak is in early access. Please don't enter real medical,
              school, or clinical information. <Link to="/demo-safety" className="underline font-semibold">Why →</Link>
            </p>
          )}
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="display font-semibold mb-1 block">Your name</span>
            <input
              className="brutal-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={80}
              data-testid="register-name"
            />
          </label>
          <label className="block">
            <span className="display font-semibold mb-1 block">Email</span>
            <input
              className="brutal-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="register-email"
            />
          </label>
          <label className="block">
            <span className="display font-semibold mb-1 block">Password</span>
            <input
              className="brutal-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              data-testid="register-password"
            />
          </label>

          {error && (
            <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3" data-testid="register-error">
              <div className="font-bold text-base">{error}</div>
              {/already registered/i.test(error) && (
                <div className="text-sm mt-2">
                  This email is already on SonSpeak.{" "}
                  <Link to={`/login?email=${encodeURIComponent(email)}`} className="font-bold underline">
                    Sign in instead →
                  </Link>
                </div>
              )}
            </div>
          )}

          <div className="brutal-card p-3 bg-[color:var(--paper-muted)] text-sm" data-testid="register-region-card">
            <div className="display font-semibold text-xs uppercase tracking-[0.18em] text-[color:var(--coral-deep)] mb-2">
              Where are you?
            </div>
            <div className="grid grid-cols-3 gap-2">
              <label className="block col-span-2">
                <span className="text-xs text-[color:var(--ink-soft)] mb-1 block">Country</span>
                <select
                  className="brutal-input"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  data-testid="register-country"
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada (waitlist)</option>
                  <option value="GB">United Kingdom (waitlist)</option>
                  <option value="AU">Australia (waitlist)</option>
                  <option value="OTHER">Other / not listed (waitlist)</option>
                </select>
              </label>
              {country === "US" && (
                <label className="block">
                  <span className="text-xs text-[color:var(--ink-soft)] mb-1 block">State</span>
                  <input
                    className="brutal-input uppercase"
                    value={stateProvince}
                    onChange={(e) => setStateProvince(e.target.value.slice(0, 2).toUpperCase())}
                    placeholder="CA"
                    maxLength={2}
                    data-testid="register-state"
                  />
                </label>
              )}
            </div>
            {country !== "US" && (
              <p className="text-xs text-[color:var(--ink-soft)] mt-2 leading-snug" data-testid="register-non-us-notice">
                SonSpeak is currently only available in the United States. Pick your
                country and we'll add you to the waitlist for when we launch there.
              </p>
            )}
          </div>

          <div className="brutal-card p-3 bg-[color:var(--paper-muted)] text-sm" data-testid="register-caregiver-consent-card">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={caregiverConsent}
                onChange={(e) => setCaregiverConsent(e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-[color:var(--coral)]"
                required
                data-testid="register-caregiver-consent"
              />
              <span className="leading-snug">
                I confirm I am <strong>18 or older</strong> and am the parent, legal guardian, adult caregiver, or authorized professional responsible for this account.
              </span>
            </label>
          </div>

          {/* Required early-access acknowledgment — must be checked before an
              account (and profile) is created. */}
          <div
            className="brutal-card p-3 text-sm"
            style={{ background: "#FFF0EE", borderColor: "var(--coral-deep)" }}
            data-testid="register-early-access-card"
          >
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={earlyAccessAck}
                onChange={(e) => setEarlyAccessAck(e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-[color:var(--coral)]"
                required
                data-testid="register-early-access-ack"
              />
              <span className="leading-snug" style={{ color: "var(--ink)" }}>
                I understand SonSpeak is in <strong>early access</strong> and is{" "}
                <strong>not a medical, clinical, or emergency tool</strong>. For
                real-world safety or emergencies I'll contact a doctor or 911.{" "}
                <Link to="/demo-safety" className="underline font-semibold" style={{ color: "var(--coral-deep)" }}>
                  Read this first →
                </Link>
              </span>
            </label>
          </div>

          <button
            type="submit"
            className="pill-btn primary w-full"
            disabled={busy}
            data-testid="register-submit"
          >
            {busy ? "Creating…" : "Create account"}
          </button>
        </form>

        <div className="text-center mt-5 text-sm text-[color:var(--ink-soft)]">
          Already have one?{" "}
          <Link to="/login" className="text-[color:var(--coral-deep)] font-bold underline" data-testid="register-to-login">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
